"use client";

import { useState } from "react";
import { IconButton } from "@/components/ui/buttons";
import { Chip } from "@/components/ui/Chip";
import { BorderGlow } from "@/components/animations/BorderGlow";
import { Textfield } from "@/components/ui/form/Textfield";
import { agentStore } from "@/features/agent";
import { getTranslations, Locale } from "@/lib/i18n/config";
import { SlideUpIn } from "../animations";
import { pushToDataLayer } from "@/lib/analytics/pushToDataLayer";

export interface AgentControlBlockProps {
  promptSuggestions?: string[];
  className?: string;
  locale?: Locale;
}

export const AgentControlBlock = ({
  locale = "de",
  promptSuggestions = [],
  className = "",
}: AgentControlBlockProps) => {
  const { agent: t } = getTranslations(locale);
  const [message, setMessage] = useState("");

  const chipAnimationDelay = 3;

  const onSendMessage = (nextMessage: string) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    agentStore.open();
    agentStore.sendUserMessage(nextMessage);
    window.dispatchEvent(new Event("agent:focus-input"));

    window.requestAnimationFrame(() => {
      const introAnchor =
        document.getElementById("Intro") ?? document.getElementById("intro");

      introAnchor?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const handleSend = () => {
    const normalizedMessage = message.trim();
    if (!normalizedMessage) return;

    pushToDataLayer({ event: "agent_searchfield" });
    onSendMessage(normalizedMessage);
    setMessage("");
  };

  function handleClickSuggestion(suggestion: string) {
    pushToDataLayer({
      event: "agent_searchfield_filter",
      click_text: suggestion,
    });
    onSendMessage(suggestion);
  }

  return (
    <div className={`flex flex-col gap-md md:gap-xl ${className}`}>
      <div className="flex gap-2">
        <SlideUpIn delay={2} className="w-full">
          <Textfield
            name={t.messageLabel}
            color={"dark"}
            placeholder={t.messagePlacholder}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSend();
              }
            }}
          ></Textfield>
        </SlideUpIn>
        <SlideUpIn delay={2.3}>
          <IconButton
            icon="paperplane"
            color="dark"
            ariaLabel={t.sendMessage}
            onClick={handleSend}
          />
        </SlideUpIn>
      </div>
      <div className="flex flex-nowrap overflow-x-auto md:flex-wrap md:justify-center gap-2 py-4 -my-4 px-4 -mx-4 overflow-y-hidden">
        {promptSuggestions.map((suggestion, index) => (
          <SlideUpIn
            key={index}
            delay={chipAnimationDelay + index * 0.1}
            className="shrink-0"
          >
            <BorderGlow
              key={index}
              color="purple"
              backgroundColor="transparent"
              className="rounded-full pointer-events-auto md:inline-grid"
              glowRadius={10}
              glowIntensity={0.4}
              coneSpread={20}
              edgeSensitivity={0}
            >
              <Chip
                icon="message-bubble"
                color={"white-gradient"}
                onClick={() => handleClickSuggestion(suggestion)}
                className="text-nowrap md:text-wrap"
              >
                {suggestion}
              </Chip>
            </BorderGlow>
          </SlideUpIn>
        ))}
        {/* Spacer to prevent last item clipping */}
        <div className="w-4 shrink-0 md:hidden" aria-hidden="true" />
      </div>
    </div>
  );
};
